"use client";

import { useCallback } from "react";
import { useRouter } from "next/navigation";
import Link from "next/link";
import {
  ArrowLeft,
  ArrowRight,
  ScanLine,
  Loader2,
  FileText,
  Upload,
  Type,
  Zap,
  ShieldCheck,
  Lock,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { DocumentUploader } from "@/components/upload/document-uploader";
import { DocumentTypeSelector } from "@/components/scan/document-type-selector";
import { WizardSteps } from "@/components/scan/wizard-steps";
import { StepDetect } from "@/components/scan/step-detect";
import { StepLanguage } from "@/components/scan/step-language";
import { useScanWizard } from "@/hooks/use-scan-wizard";
import { cn } from "@/lib/utils";
import type { Locale } from "@/i18n/config";

export default function FreeScanPage() {
  const router = useRouter();
  const wizard = useScanWizard("english");

  const handleStartScan = useCallback(async () => {
    wizard.setScanning(true);
    wizard.setError(null);

    try {
      let response: Response;

      if (wizard.inputMode === "text") {
        // Text paste mode — send JSON
        response = await fetch("/api/scan/free", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            text: wizard.pastedText,
            documentType: wizard.documentType,
            language: wizard.language,
          }),
        });
      } else {
        // File upload mode — send FormData
        const formData = new FormData();
        formData.append("file", wizard.files[0]);
        formData.append("documentType", wizard.documentType);
        formData.append("language", wizard.language);
        response = await fetch("/api/scan/free", {
          method: "POST",
          body: formData,
        });
      }

      const data = await response.json();

      if (!response.ok) {
        wizard.setError(
          data.rateLimited
            ? data.error
            : data.error || "Something went wrong."
        );
        wizard.setScanning(false);
        return;
      }

      router.push(`/try/results/${data.scanId}`);
    } catch {
      wizard.setError("Network error. Please check your connection.");
      wizard.setScanning(false);
    }
  }, [wizard, router]);

  return (
    <div className="min-h-screen bg-background">
      <main className="max-w-2xl mx-auto px-4 py-8 space-y-6">
        {/* Hero */}
        <div className="text-center space-y-2">
          <div className="inline-flex items-center gap-1.5 rounded-full bg-brand/10 px-3 py-1 text-xs text-brand font-medium">
            <Zap size={12} />
            Free — no account needed
          </div>
          <h1 className="text-2xl font-bold text-foreground">
            Scan your contract for red flags
          </h1>
          <p className="text-sm text-muted-foreground max-w-md mx-auto">
            Upload a document or paste text. Our AI analyzes it against Indian law in under 3 minutes.
          </p>
        </div>

        {/* Wizard step indicator */}
        <WizardSteps
          currentStep={wizard.step}
          stepIndex={wizard.stepIndex}
        />

        {/* Step content */}
        <div
          key={wizard.step}
          className="animate-in fade-in slide-in-from-right-4 duration-300"
        >
          {/* Step 1: Upload or Paste */}
          {wizard.step === "upload" && (
            <div className="space-y-4">
              {/* Input mode toggle */}
              <div className="flex gap-2 rounded-lg bg-card border border-border p-1">
                <button
                  onClick={() => wizard.setInputMode("file")}
                  className={cn(
                    "flex-1 flex items-center justify-center gap-2 rounded-md px-3 py-2 text-xs font-medium transition-colors",
                    wizard.inputMode === "file"
                      ? "bg-accent text-foreground shadow-sm"
                      : "text-muted-foreground hover:text-foreground"
                  )}
                >
                  <Upload size={14} />
                  Upload file
                </button>
                <button
                  onClick={() => wizard.setInputMode("text")}
                  className={cn(
                    "flex-1 flex items-center justify-center gap-2 rounded-md px-3 py-2 text-xs font-medium transition-colors",
                    wizard.inputMode === "text"
                      ? "bg-accent text-foreground shadow-sm"
                      : "text-muted-foreground hover:text-foreground"
                  )}
                >
                  <Type size={14} />
                  Paste text
                </button>
              </div>

              {wizard.inputMode === "file" ? (
                <DocumentUploader
                  mode="single"
                  maxSizeMb={10}
                  onFilesSelected={(f) => wizard.setFiles(f)}
                />
              ) : (
                <div className="space-y-2">
                  <textarea
                    value={wizard.pastedText}
                    onChange={(e) => wizard.setPastedText(e.target.value)}
                    placeholder={"Paste your contract, rental agreement, employment terms, or any legal document...\n\nMinimum 50 characters required."}
                    className="w-full min-h-[240px] rounded-lg border border-border bg-card p-4 text-sm text-foreground placeholder:text-muted-foreground/50 resize-y focus:outline-none focus:ring-2 focus:ring-brand/30 focus:border-brand/50"
                  />
                  <div className="flex items-center justify-between text-xs text-muted-foreground">
                    <span>
                      {wizard.pastedText.length > 0
                        ? `${wizard.pastedText.trim().length} characters`
                        : "Minimum 50 characters"}
                    </span>
                    {wizard.pastedText.trim().length > 0 &&
                      wizard.pastedText.trim().length < 50 && (
                        <span className="text-amber-500">
                          Need at least 50 characters
                        </span>
                      )}
                  </div>
                </div>
              )}

              <div className="flex justify-end">
                <Button
                  onClick={wizard.next}
                  disabled={!wizard.canGoNext()}
                  className="bg-brand hover:bg-brand/90 text-foreground"
                >
                  Next
                  <ArrowRight className="h-4 w-4 ml-1.5" />
                </Button>
              </div>
            </div>
          )}

          {/* Step 2: Smart Detection (file mode only) */}
          {wizard.step === "detect" && (
            <div className="space-y-4">
              <StepDetect
                file={wizard.files[0]}
                onDetected={(type) => {
                  wizard.setSuggestedType(type);
                  if (type && !wizard.documentType) {
                    wizard.setDocumentType(type);
                  }
                }}
                onDetecting={wizard.setDetecting}
              />
              <div className="flex justify-between">
                <Button
                  variant="outline"
                  onClick={wizard.back}
                  className="border-border text-foreground/80"
                >
                  <ArrowLeft className="h-4 w-4 mr-1.5" />
                  Back
                </Button>
                <Button
                  onClick={wizard.next}
                  disabled={!wizard.canGoNext()}
                  className="bg-brand hover:bg-brand/90 text-foreground"
                >
                  Next
                  <ArrowRight className="h-4 w-4 ml-1.5" />
                </Button>
              </div>
            </div>
          )}

          {/* Step 3: Document Type */}
          {wizard.step === "type" && (
            <div className="space-y-4">
              {wizard.suggestedType && (
                <div className="rounded-lg bg-brand/5 border border-brand/20 p-3 flex items-center gap-2">
                  <ScanLine size={14} className="text-brand shrink-0" />
                  <p className="text-xs text-foreground/70">
                    We detected this as a{" "}
                    <span className="font-medium">
                      {wizard.suggestedType.replace(/_/g, " ")}
                    </span>
                  </p>
                </div>
              )}
              <DocumentTypeSelector
                selected={wizard.documentType}
                onSelect={wizard.setDocumentType}
              />
              <div className="flex justify-between">
                <Button
                  variant="outline"
                  onClick={wizard.back}
                  className="border-border text-foreground/80"
                >
                  <ArrowLeft className="h-4 w-4 mr-1.5" />
                  Back
                </Button>
                <Button
                  onClick={wizard.next}
                  disabled={!wizard.canGoNext()}
                  className="bg-brand hover:bg-brand/90 text-foreground"
                >
                  Next
                  <ArrowRight className="h-4 w-4 ml-1.5" />
                </Button>
              </div>
            </div>
          )}

          {/* Step 4: Language Selection */}
          {wizard.step === "language" && (
            <div className="space-y-4">
              <StepLanguage
                selected={wizard.language}
                onSelect={wizard.setLanguage}
                currentLocale={"en" as Locale}
              />
              <div className="flex justify-between">
                <Button
                  variant="outline"
                  onClick={wizard.back}
                  className="border-border text-foreground/80"
                >
                  <ArrowLeft className="h-4 w-4 mr-1.5" />
                  Back
                </Button>
                <Button
                  onClick={wizard.next}
                  disabled={!wizard.canGoNext()}
                  className="bg-brand hover:bg-brand/90 text-foreground"
                >
                  Next
                  <ArrowRight className="h-4 w-4 ml-1.5" />
                </Button>
              </div>
            </div>
          )}

          {/* Step 5: Confirm & Scan */}
          {wizard.step === "confirm" && (
            <div className="space-y-4">
              <div className="rounded-xl bg-card border border-border p-5 space-y-4">
                <h3 className="text-sm font-semibold text-foreground">
                  Review & scan
                </h3>
                <p className="text-xs text-muted-foreground">
                  Confirm the details below and start your free scan.
                </p>

                <div className="space-y-2.5">
                  <div className="flex justify-between items-center py-1.5 border-b border-border/50">
                    <span className="text-xs text-muted-foreground">
                      Document
                    </span>
                    <span className="text-xs text-foreground font-medium flex items-center gap-1.5">
                      {wizard.inputMode === "text" ? (
                        <>
                          <Type size={12} className="text-muted-foreground" />
                          Pasted text
                        </>
                      ) : (
                        <>
                          <FileText size={12} className="text-muted-foreground" />
                          {wizard.files[0]?.name || "—"}
                        </>
                      )}
                    </span>
                  </div>
                  <div className="flex justify-between items-center py-1.5 border-b border-border/50">
                    <span className="text-xs text-muted-foreground">
                      Document type
                    </span>
                    <span className="text-xs text-foreground font-medium capitalize">
                      {wizard.documentType.replace(/_/g, " ")}
                    </span>
                  </div>
                  <div className="flex justify-between items-center py-1.5 border-b border-border/50">
                    <span className="text-xs text-muted-foreground">
                      {wizard.inputMode === "text" ? "Characters" : "File size"}
                    </span>
                    <span className="text-xs text-foreground font-medium">
                      {wizard.inputMode === "text"
                        ? `${wizard.pastedText.trim().length} chars`
                        : wizard.files[0]
                          ? `${(wizard.files[0].size / (1024 * 1024)).toFixed(1)} MB`
                          : "—"}
                    </span>
                  </div>
                  <div className="flex justify-between items-center py-1.5 border-b border-border/50">
                    <span className="text-xs text-muted-foreground">
                      Report language
                    </span>
                    <span className="text-xs text-foreground font-medium capitalize">
                      {wizard.language === "english"
                        ? "English"
                        : `English + ${wizard.language}`}
                    </span>
                  </div>
                  <div className="flex justify-between items-center py-1.5">
                    <span className="text-xs text-muted-foreground">
                      Estimated time
                    </span>
                    <span className="text-xs text-foreground font-medium">
                      1-3 minutes
                    </span>
                  </div>
                </div>
              </div>

              {/* What you'll get */}
              <div className="rounded-lg border border-border bg-card/50 p-4 space-y-2">
                <div className="flex items-center gap-2 text-xs font-medium text-foreground">
                  <ShieldCheck size={14} className="text-brand" />
                  What you&apos;ll get (free)
                </div>
                <ul className="text-xs text-muted-foreground space-y-1.5 pl-5">
                  <li>Risk score for your document</li>
                  <li>Top 5 flagged clauses with explanations</li>
                  <li>Indian law references</li>
                </ul>
                <div className="flex items-center gap-2 text-xs text-muted-foreground/70 pt-1">
                  <Lock size={12} />
                  Sign up to unlock all findings, suggested rewrites & PDF export
                </div>
              </div>

              <div className="rounded-lg bg-brand/5 border border-brand/30 p-3">
                <p className="text-[10px] text-destructive/70">
                  GotRedFlags provides AI-generated legal information, not legal advice. Always consult a qualified advocate before making legal or financial decisions.
                </p>
              </div>

              {wizard.error && (
                <div className="rounded-lg bg-destructive/10 border border-destructive/30 p-3 text-xs text-destructive">
                  {wizard.error}
                  {wizard.error.includes("Sign up") && (
                    <Link
                      href="/signup"
                      className="ml-2 underline font-medium"
                    >
                      Create free account
                    </Link>
                  )}
                </div>
              )}

              <div className="flex justify-between">
                <Button
                  variant="outline"
                  onClick={wizard.back}
                  className="border-border text-foreground/80"
                  disabled={wizard.scanning}
                >
                  <ArrowLeft className="h-4 w-4 mr-1.5" />
                  Back
                </Button>
                <Button
                  onClick={handleStartScan}
                  disabled={wizard.scanning}
                  className="bg-brand hover:bg-brand/90 text-foreground"
                >
                  {wizard.scanning ? (
                    <>
                      <Loader2 className="h-4 w-4 mr-1.5 animate-spin" />
                      Starting scan...
                    </>
                  ) : (
                    <>
                      <ScanLine className="h-4 w-4 mr-1.5" />
                      Scan for free
                    </>
                  )}
                </Button>
              </div>
            </div>
          )}
        </div>

        {/* Trust signals */}
        <div className="grid grid-cols-3 gap-3 pt-4">
          <div className="rounded-lg border border-border bg-card p-3 text-center">
            <p className="text-lg font-bold text-foreground">13</p>
            <p className="text-[10px] text-muted-foreground">Languages supported</p>
          </div>
          <div className="rounded-lg border border-border bg-card p-3 text-center">
            <p className="text-lg font-bold text-foreground">100+</p>
            <p className="text-[10px] text-muted-foreground">Indian laws checked</p>
          </div>
          <div className="rounded-lg border border-border bg-card p-3 text-center">
            <p className="text-lg font-bold text-foreground">3 min</p>
            <p className="text-[10px] text-muted-foreground">Average scan time</p>
          </div>
        </div>

        {/* Disclaimer */}
        <p className="text-[10px] text-center text-muted-foreground/60 px-4">
          GotRedFlags provides AI-generated legal information, not legal advice. Always consult a qualified advocate before making legal or financial decisions.
        </p>
      </main>
    </div>
  );
}
